import { mergeStyleSets } from '@uifabric/merge-styles';
import { Depths } from '@uifabric/fluent-theme';
import theme, { layout } from './theme.style';
import { pxToRem } from './utils';

export default mergeStyleSets({
  root: {
    selectors: {
      h1: {
        ...theme.defaultFontStyle,
        fontSize: pxToRem(22),
        lineHeight: pxToRem(30),
        selectors: {
          [layout.breakpoints.min.md]: {
            fontSize: pxToRem(28),
            lineHeight: 1.29,
          },
        },
      },
      h2: {
        ...theme.defaultFontStyle,
        fontSize: pxToRem(22),
        lineHeight: pxToRem(30),
        fontWeight: 600,
      },
      h3: {
        ...theme.defaultFontStyle,
        fontSize: pxToRem(16),
        lineHeight: pxToRem(22),
        fontWeight: 600,
      },
      h4: {
        ...theme.defaultFontStyle,
        fontSize: pxToRem(14),
        lineHeight: pxToRem(20),
        fontWeight: 600,
      },

      // grid overrides
      '.ms-Grid': {
        padding: '0 16px',
        margin: '0 -16px',
        display: 'flex',
      },
      '.ms-Grid-row': {
        margin: '0 -16px',
        flex: 1,
      },
      '.ms-Grid-col': {
        paddingLeft: '16px',
        paddingRight: '16px',
      },

      // Pivot (Tabs) overrides
      '.ms-Pivot': {
        borderBottom: `2px solid ${theme.palette.neutralLight}`,
        selectors: {
          '.ms-Button': {
            padding: 0,
            marginRight: pxToRem(16),
            height: pxToRem(30),
            lineHeight: pxToRem(30),
            selectors: {
              [layout.breakpoints.min.md]: {
                marginRight: pxToRem(40),
              },
            },
          },
          '.ms-Button:last-child': {
            marginRight: 0,
          },
          '.ms-Button a': {
            display: 'inline-block',
            lineHeight: pxToRem(30),
            color: theme.palette.neutralTertiary,
          },
          '.ms-Button.is-selected a': {
            color: theme.palette.neutralDark,
          },
          '.ms-Button a:hover': {
            color: theme.palette.neutralDark,
            textDecoration: 'none',
          },
          '.ms-Button-flexContainer': {
            padding: 0,
            justifyContent: 'center',
          },
          '.ms-Pivot-link:hover, .ms-Pivot-link:hover': {
            background: 'none',
          },
          '.ms-Pivot-link.is-selected::before': {
            left: 0,
            right: 0,
            bottom: '-2px',
            transition: 'none',
          },
        },
      },

      // Buttons
      // Primary
      '.ms-Button--primary': {
        maxWidth: 'fit-content',
        padding: `${pxToRem(7)} ${pxToRem(20)}`,
        boxShadow: Depths.depth4,
        border: 'none',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        selectors: {
          ':hover': {
            backgroundColor: theme.palette.themeDarkerAlt,
            border: 'none',
          },
          ':focus': {
            outline: `thin dotted ${theme.palette.neutralQuaternary}`,
          },
          ':active': {
            backgroundColor: theme.palette.themeDarker,
            border: 'none',
          },
          '&.is-disabled': {
            backgroundColor: theme.palette.themeDarker,
            border: 'none',
          },
          [layout.breakpoints.max.lg]: {
            fontSize: pxToRem(12),
            whiteSpace: 'normal',
            lineHeight: 'normal',
          },
        },
      },
      // Secondary (use DefaultButton for this)
      '.ms-Button--default': {
        maxWidth: 'fit-content',
        padding: `${pxToRem(7)} ${pxToRem(20)}`,
        border: `solid ${pxToRem(1)} ${theme.palette.neutralTertiary}`,
        backgroundColor: theme.palette.neutralWhite,
        selectors: {
          ':hover': {
            backgroundColor: theme.palette.neutralLighter,
          },
          ':focus': {
            backgroundColor: theme.palette.neutralWhite,
            outline: `thin dotted ${theme.palette.neutralQuaternary}`,
          },
          ':active': {
            backgroundColor: theme.palette.neutralLight,
          },
        },
      },
      // disabled
      '.ms-Button.is-disabled': {
        backgroundColor: theme.palette.neutralLighter,
        color: theme.palette.neutralTertiary,
        boxShadow: 'none',
        border: 'none',
      },

      '.ms-TextField.is-disabled': {
        selectors: {
          '.ms-Label': {
            color: theme.palette.neutralPrimary,
          },
        },
      },

      // Fix padding for labels to match the mocks
      '.ms-Label': {
        paddingTop: 0,
      },
      '.ms-MessageBar': {
        padding: pxToRem(16),
        selectors: {
          '.ms-MessageBar-innerText': {
            fontSize: pxToRem(14),
            lineHeight: pxToRem(20),
            fontWeight: 600,
            color: theme.palette.neutralDark,
          },
          '.ms-Link': {
            textDecoration: 'none',
          },
          '.ms-Link:hover': {
            textDecoration: 'underline',
          },
          '.ms-MessageBar-icon': {
            margin: 0,
          },
          '.ms-MessageBar-text': {
            margin: `0 0 0 ${pxToRem(12)}`,
          },
          '.ms-MessageBar-icon i': {
            fontSize: pxToRem(16),
            lineHeight: pxToRem(16),
            height: pxToRem(16),
            top: pxToRem(2),
            position: 'relative',
            color: theme.palette.neutralDark,
          },
          '.ms-MessageBar-dismissal': {
            height: '100%',
            padding: '1px 6px',
          },
          '.ms-MessageBar-dismissal i': {
            color: theme.palette.neutralWhite,
            fontSize: pxToRem(12),
            lineHeight: pxToRem(12),
            height: pxToRem(12),
          },
        },
      },
      '.ms-MessageBar--error': {
        background: theme.palette.accentMessage,
        selectors: {
          '.ms-MessageBar-innerText': {
            fontSize: pxToRem(14),
            lineHeight: pxToRem(20),
            fontWeight: 600,
            color: theme.palette.neutralWhite,
          },
          '.ms-MessageBar-icon i': {
            color: theme.palette.neutralWhite,
          },
        },
      },
      '.ms-MessageBar--success': {
        background: theme.palette.accentTeal,
        selectors: {
          '.ms-MessageBar-innerText': {
            fontSize: pxToRem(14),
            lineHeight: pxToRem(20),
            fontWeight: 600,
            color: theme.palette.neutralWhite,
          },
          '.ms-MessageBar-icon i': {
            color: theme.palette.neutralWhite,
          },
        },
      },
      '.ms-List::after': {
        backgroundImage:
          'linear-gradient(transparent 30%, rgba(255, 255, 255, 0.4) 65%, rgb(255, 255, 255) 100%)',
      },
      '.ms-ShimmerLine-root': {
        borderColor: 'white',
      },
      '.ms-ShimmerGap-root': {
        borderColor: 'white',
        background: 'white',
      },

      // color ratio
      '.ms-Persona-initials': {
        color: theme.palette.neutralWhite,
      },

      // button outline on high contrast mode
      '@media(forced-colors: active)': {
        '.ms-Button--primary:focus': {
          outline: `solid ${pxToRem(2)} #73e4f6`,
        },
      },
    },
  },
  mainContentContainer: {
    backgroundColor: theme.palette.white,
    position: 'relative',
    left: 0,
    marginTop: pxToRem(layout.constants.topNav.height + layout.constants.commandBar.height),
    minHeight: 'calc(100vh - 5.8125rem)',
    height: '100%',
    width: '100%',
  },
  mobileApp: {
    marginTop: 0,
  },
  smallScreen: {
    position: 'absolute',
    height: 'auto',
  },
  inSetupColor: {
    color: theme.palette.accentError,
  },
  setupCompleteColor: {
    color: theme.palette.accentYellow,
  },
  activeColor: {
    color: theme.palette.accentTeal,
  },
  pendingColor: {
    color: theme.palette.accentYellow,
  },
  expiredColor: {
    color: theme.palette.neutralTertiaryAlt,
  },
  defaultColor: {
    color: theme.palette.accentError,
  },
});
